/**
 * ECOPD-18285 — TTAuth (GCIP) sign-in for the Dispatcher UI.
 *
 * The Dispatcher BFF owns everything security-relevant: it reverse-proxies TTAuth on this
 * origin (`/ttauth/v1/*`, so the TTAuth session cookie is first-party), verifies the
 * cookie, exchanges it with the agency's K3 for a normal BFF session, and decides per
 * agency (from the agency's K3 flag `component.auth.ttauth.enabled`) whether TTAuth is
 * offered at all. This module only:
 *   - asks the BFF which sign-in options this host has   (GET  /drt/api/bff/auth/config)
 *   - turns a TTAuth sign-in into a BFF session           (POST /drt/api/bff/ttauth/exchange)
 *   - builds the TTAuth sign-in / sign-out URLs
 *   - loads TTAuth's session script (SharedWorker that keeps the cookie fresh)
 *
 * Loop safety (lesson from transitos AuthProvider): nothing here ever navigates to TTAuth
 * on its own. A failed exchange or a TTAuth worker event never signs the user out; only
 * the user's own click goes to TTAuth, and only an authoritative 401 ends a session.
 */
import { apiRoutes } from "@/shared/api/routes";

/**
 * How users sign in on this host: the Dispatcher login page (`password`), or TTAuth only
 * (`ttauth`: no Dispatcher login page, `/login` hands over to the TTAuth sign-in page).
 */
export type LoginMode = "password" | "ttauth";

/** A TTAuth sign-in failure as reported by the BFF (`errors[0]`). */
export interface TtauthLoginError {
  code: string;
  message: string;
  /** True: the browser must go through TTAuth sign-out, or TTAuth signs it straight back in. */
  signOut: boolean;
}

export type TtauthExchangeResult =
  | { ok: false; reason: "error"; error: TtauthLoginError }
  /** No usable TTAuth sign-in in this browser: show the login page, no error. */
  | { ok: false; reason: "not_signed_in" }
  | { ok: true; session: unknown };

const SESSION_SCRIPT_PATH = "/ttauth/v1/cookie/init.js";
const SESSION_SCRIPT_ID = "ttauth-session-script";

/**
 * Loop guard for the automatic hand-over to TTAuth. If the browser comes back from TTAuth
 * and still has no session (e.g. K3 rejects the cookie), `/login` must not send it
 * straight back: TTAuth would bounce it again at once, forever. One automatic hand-over
 * per window; after that the user decides (Try again / Sign out).
 */
const REDIRECT_MARK_KEY = "ttauth:lastRedirectAt";
const REDIRECT_WINDOW_MS = 60_000;

/** Codes that mean "this browser is not signed in to TTAuth" rather than a failure to show. */
const NOT_SIGNED_IN_CODES = new Set(["TTAUTH_COOKIE_MISSING", "TTAUTH_COOKIE_INVALID"]);

interface BffErrorBody {
  errors?: { code?: string; message?: string; sign_out?: boolean }[];
}

/** First `{ code, message, sign_out }` of a BFF error body, or null. */
export function readBffError(body: unknown): TtauthLoginError | null {
  const first = (body as BffErrorBody | null)?.errors?.[0];
  if (!first?.code) {
    return null;
  }
  return { code: first.code, message: first.message ?? first.code, signOut: first.sign_out === true };
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Sign-in options for this host. A failure is returned as an error (shown on the login
 * page), never silently turned into a mode.
 */
export async function fetchLoginMode(): Promise<
  { ok: false; error: TtauthLoginError } | { ok: true; mode: LoginMode }
> {
  try {
    const response = await fetch(apiRoutes.bff("auth/config"), {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    const body = await readJson(response);
    const mode = (body as { login_mode?: unknown } | null)?.login_mode;
    if (response.ok && (mode === "password" || mode === "ttauth")) {
      return { ok: true, mode };
    }
    return {
      ok: false,
      error: readBffError(body) ?? {
        code: `HTTP_${response.status}`,
        message: "Could not load the sign-in options.",
        signOut: false,
      },
    };
  } catch {
    return {
      ok: false,
      error: { code: "NETWORK_ERROR", message: "Could not load the sign-in options.", signOut: false },
    };
  }
}

/**
 * Ask TTAuth to renew its session cookie (GET /ttauth/v1/cookie/me). TTAuth's access cookie
 * lives 15-60 min and is only kept fresh by its SharedWorker while a tab is open; after the
 * laptop slept or every tab was closed it has expired, while the 30-day refresh cookie (sent
 * only to this path) is still good. Calling this before the exchange renews it silently
 * instead of bouncing the user through the TTAuth sign-in page. The result is not needed:
 * the exchange reports whether a usable sign-in exists.
 */
export async function refreshTtauthCookie(): Promise<void> {
  try {
    await fetch("/ttauth/v1/cookie/me", { credentials: "include", headers: { Accept: "application/json" } });
  } catch {
    // The exchange that follows reports the outcome.
  }
}

/** Exchange the browser's TTAuth sign-in for a BFF session (sets the BFF session cookie). */
export async function exchangeTtauthSession(): Promise<TtauthExchangeResult> {
  let response: Response;
  try {
    response = await fetch(apiRoutes.bff("ttauth/exchange"), {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
  } catch {
    return {
      ok: false,
      reason: "error",
      error: { code: "NETWORK_ERROR", message: "Could not reach the server. Please try again.", signOut: false },
    };
  }
  const body = await readJson(response);
  if (response.ok) {
    return { ok: true, session: body };
  }
  const error = readBffError(body) ?? {
    code: `HTTP_${response.status}`,
    message: "Single sign-on failed. Please try again.",
    signOut: false,
  };
  if (NOT_SIGNED_IN_CODES.has(error.code)) {
    return { ok: false, reason: "not_signed_in" };
  }
  return { ok: false, reason: "error", error };
}

let signInInFlight: Promise<TtauthExchangeResult> | null = null;

/**
 * Renew the TTAuth cookie, then exchange it for a BFF session - one at a time. Callers that
 * arrive while an exchange is running share its result instead of starting another: every
 * K3 exchange issues the user a new K3 token, so a second, parallel exchange invalidates
 * the session the first one just created (its K3 calls fail with 401 and start yet another
 * exchange). Parallel callers are normal: React StrictMode runs the start-up effect twice,
 * and the 401s of requests sent before the session existed each trigger a session check.
 */
export function signInWithTtauth(): Promise<TtauthExchangeResult> {
  if (!signInInFlight) {
    signInInFlight = (async () => {
      await refreshTtauthCookie();
      return exchangeTtauthSession();
    })().finally(() => {
      signInInFlight = null;
    });
  }
  return signInInFlight;
}

/** Record that the browser is being sent to TTAuth sign-in now. */
export function markTtauthRedirect(): void {
  try {
    sessionStorage.setItem(REDIRECT_MARK_KEY, String(Date.now()));
  } catch {
    // Storage unavailable: the guard cannot work, so callers must not auto-redirect.
  }
}

/** Forget the hand-over mark (a session was established). */
export function clearTtauthRedirectMark(): void {
  try {
    sessionStorage.removeItem(REDIRECT_MARK_KEY);
  } catch {
    // ignore
  }
}

/**
 * May `/login` hand over to TTAuth automatically? No when it already did within the
 * window, and no when storage is unavailable (the loop could not be detected).
 */
export function mayAutoRedirectToTtauth(now = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(REDIRECT_MARK_KEY) ?? 0);
    return !(last > 0 && now - last < REDIRECT_WINDOW_MS);
  } catch {
    return false;
  }
}

/** TTAuth Hosted UI sign-in; TTAuth brings the browser back to `returnPath` on this origin. */
export function ttauthLoginUrl(returnPath: string): string {
  const path = returnPath.startsWith("/") ? returnPath : `/${returnPath}`;
  const returnUrl = `${window.location.origin}${path}`;
  return `/ttauth/v1/cookie/login-redirect?returnUrl=${encodeURIComponent(returnUrl)}`;
}

/** TTAuth sign-out (clears its cookie), then back to this app's login page. */
export function ttauthLogoutUrl(): string {
  const returnUrl = `${window.location.origin}/login`;
  return `/ttauth/v1/cookie/logout?returnUrl=${encodeURIComponent(returnUrl)}`;
}

/** TTAuth's change-password page (it emails a change-password link). */
export function ttauthChangePasswordUrl(email?: string): string {
  const params = new URLSearchParams({ returnUrl: window.location.href });
  if (email) {
    params.set("email", email);
  }
  return `/ttauth/v1/cookie/change-password-redirect?${params.toString()}`;
}

/**
 * Load TTAuth's session script once. It connects to TTAuth's SharedWorker, which swaps the
 * TTAuth cookie before it expires while any tab is open. Its events are deliberately not
 * acted on: a refresh hiccup is not a sign-out (only an authoritative 401 is).
 */
export function loadTtauthSessionScript(): void {
  if (typeof document === "undefined" || document.getElementById(SESSION_SCRIPT_ID)) {
    return;
  }
  const script = document.createElement("script");
  script.id = SESSION_SCRIPT_ID;
  script.src = SESSION_SCRIPT_PATH;
  script.async = true;
  document.head.appendChild(script);
}
